import React, { useCallback, useContext, useEffect, useState } from 'react';
import { LocalizationContext } from 'app/localization/Context';
import { v4 as uuidv4 } from 'uuid';
import { OTAProgressPhase, OTAProgressProgress } from 'app/model/OTAProgress';
import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';


import { ReactComponent as IconX } from 'images/general/x.svg';
import { ReactComponent as IconDownloading } from 'images/general/downloading.svg';
import { ReactComponent as IconInstalling } from 'images/general/gear-animated.svg';
import { ReactComponent as IconTick } from 'images/general/tick-white.svg';

import './OTAReport.scss'

export const OTAType = {
    device: 0,
    hub: 1
}

export function useOTAReportPage(otaProgressController, otaType, deviceStartOTAFunc) {
    const hs = useContext(HistoryStackContext);

    const notificationCallbackId = useState(uuidv4())[0];

    const startOTA = useCallback(() => {
        if (otaType === OTAType.device) {
            deviceStartOTAFunc(notificationCallbackId);
        } else if (otaType === OTAType.hub) {
            if ((otaProgressController !== undefined) && (otaProgressController.startHubOTA !== undefined)) {
                otaProgressController.startHubOTA();
            }
        }
        hs.push("/ota-report", {
            otaType: otaType,
            notificationCallbackId: notificationCallbackId,
        })
    }, [hs, notificationCallbackId, otaProgressController, deviceStartOTAFunc, otaType]);

    return startOTA;
}

export function representOTAState(o, localizeNumber, otaState) {
    let error = true;
    let mainIcon = IconX;
    let title = o.title.error;
    let info = o.infoError;
    let isProgressActive = false;
    let progressValue = 0;
    let progressTitle = "";
    let progressFullText = "";

    switch (otaState.phase) {
        case OTAProgressPhase.download:
            switch (otaState.progress) {
                case OTAProgressProgress.started:
                    error = false;
                    mainIcon = IconDownloading;
                    title = o.title.downloading;
                    info = o.infoGeneral;
                    break;
                case OTAProgressProgress.inProgress:
                    error = false;
                    mainIcon = IconDownloading;
                    title = o.title.downloading;
                    info = o.infoGeneral;
                    isProgressActive = true;
                    progressValue = otaState.progress_value / 2;
                    progressTitle = title;
                    break;
                case OTAProgressProgress.finished:
                case OTAProgressProgress.rebooting:
                    error = false;
                    mainIcon = IconDownloading;
                    title = o.title.downloading;
                    info = o.infoGeneral;
                    isProgressActive = true;
                    progressValue = 50;
                    progressTitle = title;
                    break;
                case OTAProgressProgress.failed:
                default:
                    break;
            }
            break;
        case OTAProgressPhase.install:
            switch (otaState.progress) {
                case OTAProgressProgress.started:
                    error = false;
                    mainIcon = IconInstalling;
                    title = o.title.installing;
                    info = o.infoGeneral;
                    break;
                case OTAProgressProgress.inProgress:
                    error = false;
                    mainIcon = IconInstalling;
                    title = o.title.installing;
                    info = o.infoGeneral;
                    isProgressActive = true;
                    progressValue = 50 + (otaState.progress_value / 2);
                    progressTitle = title;
                    break;
                case OTAProgressProgress.finished:
                case OTAProgressProgress.rebooting:
                    error = false;
                    mainIcon = IconInstalling;
                    title = o.title.installing;
                    info = o.infoGeneral;
                    isProgressActive = true;
                    progressValue = 100;
                    progressTitle = title;
                    break;
                case OTAProgressProgress.failed:
                default:
                    break;
            }
            break;
        case OTAProgressPhase.afterReboot:
            switch (otaState.progress) {
                case OTAProgressProgress.started:
                case OTAProgressProgress.rebooting:
                case OTAProgressProgress.inProgress:
                    error = false;
                    mainIcon = IconInstalling;
                    title = o.title.installing;
                    info = o.infoGeneral;
                    isProgressActive = true;
                    progressValue = 100;
                    progressTitle = title;
                    break;
                case OTAProgressProgress.finished:
                    error = false;
                    mainIcon = IconTick;
                    title = o.title.done;
                    info = o.infoDone;
                    break;
                case OTAProgressProgress.failed:
                default:
                    break;
            }
            break;
        default:
            break;
    }

    if (isProgressActive)
        progressFullText = o.title.formatTitleValue(progressTitle, localizeNumber(String(progressValue)));

    return { error, mainIcon, title, info, isProgressActive, progressValue, progressTitle, progressFullText };
}


export function OTAReportPage({ otaProgressController, gwUpdateInfo }) {
    const p = useContext(CurrentPageContext);

    const dismiss = p.dismiss;
    const otaType = p.state.otaType;
    const notificationCallbackId = p.state.notificationCallbackId;
    const isNewerVersionAvailableForHub = gwUpdateInfo.isNewerVersionAvailable;

    const l = useContext(LocalizationContext);
    const o = l.strings.otaReport;

    const [otaState, setOTAState] = useState({ phase: 0, progress: 0, progress_value: 0 });

    useEffect(() => {
        let id = undefined;
        if (otaType === OTAType.device) {
            id = notificationCallbackId;
        } else if (otaType === OTAType.hub) {
            id = "hub";
        }

        if ((otaProgressController !== undefined) && (otaProgressController.registerNotification !== undefined)) {
            otaProgressController.registerNotification(id, setOTAState);
        }
        return () => {
            if ((otaProgressController !== undefined) && (otaProgressController.unregisterNotification !== undefined)) {
                otaProgressController.unregisterNotification(id);
            }
        }
    }, [otaType, otaProgressController, notificationCallbackId, setOTAState]);

    let { error, mainIcon, title, info,
        isProgressActive, progressValue, progressFullText } = representOTAState(o, l.localizeNumber, otaState);

    if (otaType === OTAType.hub) {
        if (isNewerVersionAvailableForHub !== true) { // finished
            error = false;
            mainIcon = IconTick;
            title = o.title.done;
            info = o.infoDone;
            isProgressActive = false;
        }
    }

    return (
        <div className="device-detail-ota-report-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />

            <div className="bg-blank" style={{ zIndex: -9, opacity: error ? 1 : 0, transition: "opacity 0.5s linear" }} />
            <div className="bg-failure-color" style={{ zIndex: -8, opacity: error ? 1 : 0, transition: "opacity 0.5s linear" }} />

            <div className="device-detail-ota-report-page-content">
                <IconX className="nav-icon" /> <div className="nav-button" onClick={dismiss} />

                {React.createElement(mainIcon, { className: "main-icon" })}
                <div className="title">{title}</div>
                <div className="info">{info}</div>

                <div />

                {
                    isProgressActive ?
                        <div className="progress-bar-container" key={"ota-progress-phase-" + String(otaState.phase)}>
                            <div className="progress-bar" style={{
                                width: String(progressValue) + "%"
                            }} />
                            <div className="text">{progressFullText}</div>
                        </div> : null
                }
            </div>
        </div>
    );
}